/**
 * note.ts — the note de calcul (P1 US-116, FR-28).
 *
 * Ported from oracle lines 1157–1194: `noteHTML` and `imprimer`.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * THIS IS THE ARTEFACT WITH THE LOWEST TOLERANCE FOR DRIFT IN THE WHOLE PRODUCT
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * The note is what an engineer puts their name to and hands to a control office. It is read by
 * people who were not in the room, sometimes years later, sometimes during a dispute — the
 * *garantie décennale* runs for ten years. A silent change to its wording, its section numbering
 * or its rounding is not a cosmetic regression: it makes two documents that describe the same
 * calculation look like they describe different ones.
 *
 * `test/note.test.ts` renders the note for one fixture per module and compares it byte for byte
 * against output generated by the FROZEN ORACLE's own `noteHTML`. That comparison is the only
 * mechanism that makes "we did not change the note" a checkable claim, and it is why every string
 * below is copied rather than rewritten — including the unaccented spelling, which must not be
 * corrected.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * ONE RENDERER, TWO STYLESHEETS
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * The note reuses `barres`, `blocELS` and the `details*` functions from the screen panel, exactly
 * as the oracle does. The printed document and the on-screen results are the same HTML with
 * different CSS applied — that is what guarantees the note cannot disagree with what the engineer
 * was looking at when they decided to print it.
 */

import type { CalcResponse } from '@ronceray/contract';

import { detailsFor, type Values } from './details';
import { must } from './dom';
import { FICHES, type FicheModule } from './fiches';
import { ratio3 } from './format';
import { barres, blocELS, globalRatio } from './render';
import { checkStatusFromRatio } from '@ronceray/contract';

/** The last computed state for a module — the oracle's `DERNIER`, line 1051. */
export interface LastResult {
  readonly input: Record<string, unknown>;
  readonly envelope: CalcResponse;
}

/**
 * Render the note.
 *
 * Returns `''` for a module that has never been calculated, matching the oracle's
 * `if (!d) return '';`. Printing an empty note is better than printing a half-built one.
 */
export function noteHTML(
  mod: FicheModule,
  last: LastResult | undefined,
  projet: string,
  today: Date = new Date(),
): string {
  if (last === undefined) return '';

  const { input, envelope } = last;
  const fiche = FICHES[mod];

  /*
   * THE ONLY LOCALE-AWARE CALL IN THE APPLICATION.
   *
   * Everything else — every ratio, every section property — is formatted locale-independently, so
   * the same inputs print identically on any machine. The date is the deliberate exception: a
   * French note carries a French date, and `fr-FR` is pinned rather than left to the browser so
   * that a laptop configured in English does not produce a note in a different format from its
   * neighbour's.
   */
  const dt = today.toLocaleDateString('fr-FR');

  let h = '<div class="note">';

  h +=
    `<div class="n-head"><div><h1>Note de calcul - ${fiche.titre}</h1>` +
    `<p class="n-norme">${fiche.norme}</p></div>` +
    `<div class="n-meta"><b>${projet}</b><br>` +
    `Element : ${String(input['repere'] ?? '')}<br>Date : ${dt}</div></div>`;

  h += '<h2>1. Donnees d\'entree</h2><table class="vals n-in"><tbody>';
  for (const groupe of fiche.groupes) {
    h += `<tr class="grp"><td colspan="4">${groupe.titre}</td></tr>`;
    for (const champ of groupe.champs) {
      const val = input[champ.id];
      // Oracle lines 1172–1173. Three kinds of "not supplied", and all three are skipped rather
      // than printed: a note listing `Effort normal — NaN` is worse than one that omits the row.
      if (val === undefined || val === '' || (typeof val === 'number' && Number.isNaN(val))) {
        continue;
      }
      // The RAW value, not `fmt(val)`. This table restates what the engineer typed, and a value
      // rounded on its way into the record would make the note disagree with the form it came
      // from — which is the one thing a note de calcul must never do.
      h +=
        `<tr><td>${champ.l}</td><td class="sym">${champ.s ?? ''}</td>` +
        `<td class="num">${String(val)}</td><td class="u">${champ.u ?? ''}</td></tr>`;
    }
  }
  h += '</tbody></table>';

  h +=
    '<h2>2. Caracteristiques et valeurs intermediaires</h2>' +
    detailsFor(mod, envelope.values as Values);

  h += `<h2>3. Verifications a l'etat limite ultime</h2>${barres(envelope.checks)}`;

  const glob = globalRatio(envelope);
  h +=
    `<p class="n-concl ${checkStatusFromRatio(glob)}">Taux de travail maximal : ` +
    `<b>${ratio3(glob)}</b> - ${glob <= 1 ? 'SECTION VERIFIEE' : 'SECTION NON VERIFIEE'}</p>`;

  h += `<h2>4. Etat limite de service</h2>${blocELS(envelope.els)}`;

  h +=
    '<p class="n-foot">Note produite par l\'application de calcul EC3 / EC5. Les caracteristiques ' +
    'de profiles sont issues de catalogues courants et doivent etre confirmees ' +
    'sur le catalogue du fournisseur pour tout calcul definitif. Ne sont pas couverts : les ' +
    'sections de classe 4, les assemblages, ' +
    'les entailles et percements, la torsion, la fatigue et le calcul au feu detaille.</p>';

  return `${h}</div>`;
}

/**
 * Fill `#print` and open the browser's print dialog. Oracle lines 1191–1194.
 *
 * `#print` is NOT cleared afterwards, matching the oracle. That is deliberate rather than
 * overlooked: it is hidden by `#print{display:none}` on screen and only shown by the `@media print`
 * block, and leaving the last note in place means a user who hits Ctrl+P again immediately gets
 * the same document rather than a blank page.
 */
export function imprimer(mod: FicheModule, last: LastResult | undefined, projet: string): void {
  must('#print').innerHTML = noteHTML(mod, last, projet);
  window.print();
}
